import { LoaderCircle } from 'lucide-react';
import React from 'react';

import { DialogAlert } from '@/components/dialog-alert';

import { getSaveProgressStats } from './save-progress.utils';
import type { PreviewSaveStatus } from './setting-preview.types';

interface SaveProgressSummaryProps {
  progressMap: Record<string, PreviewSaveStatus>;
}

/**
 * SaveProgressSummary
 *
 * Displays a single-line status banner summarizing the state of a concurrent
 * save operation. Renders one of three visual states:
 *
 * 1. **Active** (spinner) — save in progress, shows "Saving X/Y..." with
 *    optional breakdown of completed/failed so far.
 * 2. **All succeeded** (green) — every item completed without error.
 * 3. **Partial success** (neutral) — save finished but some items failed or
 *    were cancelled. Shows "X/Y saved · Z failed · W cancelled".
 *
 * Returns `null` when no save has been initiated (empty progressMap).
 */
export const SaveProgressSummary = ({
  progressMap,
}: SaveProgressSummaryProps): React.ReactElement | null => {
  const { total, completed, failed, cancelled, settled, isActive } =
    getSaveProgressStats(progressMap);
  if (total === 0) return null;

  // ── State 1: All saved successfully (green banner) ──
  if (!isActive && failed === 0 && cancelled === 0 && completed === total) {
    return <DialogAlert variant="success">All {total} changes saved successfully.</DialogAlert>;
  }

  // ── State 2: Complete with failures or cancellations (neutral banner) ──
  if (!isActive && (failed > 0 || cancelled > 0)) {
    const lines: string[] = [];
    lines.push(`${completed}/${total} changes saved`);
    if (failed > 0) lines.push(`${failed} failed`);
    if (cancelled > 0) lines.push(`${cancelled} cancelled`);
    return <DialogAlert variant="info">{lines.join(' · ')}</DialogAlert>;
  }

  // ── State 3: Active — save still in progress (spinner + counter) ──
  const parts: string[] = [];
  if (completed > 0) parts.push(`${completed} saved`);
  if (failed > 0) parts.push(`${failed} failed`);

  return (
    <DialogAlert
      variant="info"
      icon={
        <LoaderCircle
          className="h-4 w-4 flex-shrink-0 animate-spin text-ckds-base-info"
          aria-hidden="true"
        />
      }
    >
      Saving {settled}/{total}...
      {parts.length > 0 && ` (${parts.join(', ')})`}
    </DialogAlert>
  );
};
